/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Alert, Box, Chip, DialogContentText, IconButton, Link, Stack, TableCell, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DialogDefaultV2,
  DropzoneModalV2,
  GeoPointMap,
  TableWidgetV2,
  UIconV2,
} from '../../../components/v2';
import NsmpModal from './NsmpModal';
import ServicosQuickFilters from './components/ServicosQuickFilters';
import ServicoCell from './components/ServicoCell';
import {
  applyServicosQuickFilters,
  EMPTY_SERVICOS_QUICK_FILTER,
  getTipoServicoLabel,
} from './utils/servicosFilters';
import { isAnaliseRisco, isFotoInicio, isFotoFim, isServiceRow } from './utils/nsmpRowTypes';
import {
  getFaixaInspecionadoEm,
  getFaixaServicoTitle,
  isFaixaServicoRow,
} from './utils/faixaServicosDisplay';
import {
  getServicosShowEdit,
  getServicosShowView,
  getServicosShowViewRow,
  isDigitadorEmDigitacao,
} from './utils/servicosPermissions';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsNsmp from '../../../store/nsmp/actions';
import * as Status from '../../../constants/status';
import * as Role from '../../../constants/role';
import { isBlankString, isInspecaoMtBt, isInspecaoAuditoriaFaixa, isTrue } from 'utils';
import {
  buildTrechoPoints,
  formatCoordDisplay,
  formatTrechoDistancia,
  googleMapsDirectionsUrl,
  googleMapsUrl,
  RESULTADO_AUDITORIA_LABEL,
} from '../../../helpers/auditoriaFaixa';
import { useNsmpUrlSync } from './hooks/useNsmpUrlSync';

const ServicesTab = ({
  nsmps,
  nsirId,
  updateNsmp,
  saveNsmp,
  cleanSelectedNsmp,
  nsirStatus,
  userRole,
  deleteNsmp,
  authData,
  userId,
  inspetorId,
  digitadorId,
  nsirTitle,
  tipoServicoId,
  newFlow,
  rfgImportadoViaOcr,
  cloneNsmp,
  deleteCloneNsmp,
  history,
  location,
}) => {
  const [quickFilter, setQuickFilter] = useState(EMPTY_SERVICOS_QUICK_FILTER);
  const [open, setOpen] = useState(false);
  const [openFotoInicial, setOpenFotoInicial] = useState(false);
  const [openFotoFinal, setOpenFotoFinal] = useState(false);
  const [openAnaliseRisco, setOpenAnaliseRisco] = useState(false);
  const [openClone, setOpenClone] = useState(false);
  const [openDeleteClone, setOpenDeleteClone] = useState(false);
  const [cloneId, setCloneId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [mapPoint, setMapPoint] = useState(null);

  const isAdmin = () => authData && (authData.role === Role.ADMIN || authData.role === Role.SUPER_ADMIN);
  const showAuditoriaColumn = isInspecaoMtBt(tipoServicoId);
  const isFaixa = isInspecaoAuditoriaFaixa(tipoServicoId) && isTrue(newFlow);
  const canManageFaixaCriacao = isFaixa && nsirStatus === Status.EM_CRIACAO && isAdmin();

  const filteredRows = useMemo(
    () => applyServicosQuickFilters(nsmps || [], quickFilter),
    [nsmps, quickFilter]
  );

  const handleNsmpNotInFilter = useCallback(
    () => setQuickFilter(EMPTY_SERVICOS_QUICK_FILTER),
    [],
  );

  const { openNsmpModal, closeNsmpModal, syncCarouselNsmpId } = useNsmpUrlSync({
    history,
    location,
    enabled: !!nsirId,
    allRows: nsmps,
    filteredRows,
    open,
    selectedId,
    setOpen,
    setSelectedId,
    setCurrentIndex,
    setReadOnly,
    cleanSelectedNsmp,
    onNsmpNotInFilter: handleNsmpNotInFilter,
  });

  const onEdit = (id) => {
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: userRole === Role.DIGITADOR,
      index,
    });
  };

  const onView = (id) => {
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: true,
      index,
    });
  };

  const onAdd = () => {
    openNsmpModal({
      id: null,
      readOnly: false,
      index: null,
    });
  };

  const handlerCancel = () => {
    closeNsmpModal();
  };

  const handlerConfirm = (data) => {
    closeNsmpModal();
    if (data && data.id) {
      updateNsmp({ ...data, nsirId });
    } else if (data) {
      saveNsmp({ ...data, nsirId });
    }
  };

  const goToNext = () => {
    if (currentIndex !== null && currentIndex < filteredRows.length - 1) {
      const next = filteredRows[currentIndex + 1];
      setCurrentIndex(currentIndex + 1);
      setSelectedId(next.id);
      syncCarouselNsmpId(next.id);
    }
  };

  const goToPrevious = () => {
    if (currentIndex !== null && currentIndex > 0) {
      const prev = filteredRows[currentIndex - 1];
      setCurrentIndex(currentIndex - 1);
      setSelectedId(prev.id);
      syncCarouselNsmpId(prev.id);
    }
  };

  const handlerConfirmAndNext = (data) => {
    if (data && data.id) {
      updateNsmp({ ...data, nsirId });
    } else if (data) {
      saveNsmp({ ...data, nsirId });
    }
    goToNext();
  };

  const hasNext = currentIndex !== null && currentIndex < filteredRows.length - 1;
  const hasPrevious = currentIndex !== null && currentIndex > 0;
  const carouselPosition = currentIndex !== null
    ? { current: currentIndex + 1, total: filteredRows.length }
    : null;

  const tableTitles = isFaixa
    ? ['Serviço', 'Resultado', 'Serviço informado', 'Coordenada']
    : showAuditoriaColumn
      ? ['', 'N° serviço', 'Prioridade', 'Serviço', 'Tipo Serviço', 'Nsmps', 'Auditoria']
      : ['', 'N° serviço', 'Prioridade', 'Serviço', 'Tipo Serviço', 'Nsmps'];

  const getHeaderActions = () => {
    if (isFaixa) return [];
    const actions = [];
    if (nsirStatus === Status.EM_INSPECAO && filteredRows.filter((row) => isFotoInicio(row)).length === 0) {
      actions.push({ title: 'Foto Inicial', onClick: () => setOpenFotoInicial(true) });
    }
    if (nsirStatus === Status.EM_INSPECAO && filteredRows.filter((row) => isFotoFim(row)).length === 0) {
      actions.push({ title: 'Foto Final', onClick: () => setOpenFotoFinal(true) });
    }
    if (nsirStatus === Status.EM_INSPECAO && filteredRows.filter((row) => isAnaliseRisco(row)).length === 0) {
      actions.push({ title: 'Análise de Risco', onClick: () => setOpenAnaliseRisco(true) });
    }
    return actions;
  };

  const getRowTitle = (nsmp) => {
    if (isFotoInicio(nsmp)) return 'Foto Inicial';
    if (isFotoFim(nsmp)) return 'Foto Final';
    if (isAnaliseRisco(nsmp)) return 'Análise de Risco';
    if (nsmp.fotoRegistro) return `Foto Registro ${nsmp.codigo}`;
    return `Serviço ${nsmp.codigo}`;
  };

  const renderRows = (row) => {
    if (isFaixa) {
      const isServico = isFaixaServicoRow(row);
      const servicoTitle = getFaixaServicoTitle(row);
      const inspecionadoEm = getFaixaInspecionadoEm(row);
      const resultado = row.resultadoAuditoria;
      const label = RESULTADO_AUDITORIA_LABEL[resultado] || '—';
      const color = resultado === 'VALIDO'
        ? 'success'
        : resultado === 'INVALIDO'
          ? 'error'
          : 'default';
      const points = isServico
        ? buildTrechoPoints({
          id: row.id || 'preview',
          latitude: row.latitude,
          longitude: row.longitude,
          latitudeFinal: row.latitudeFinal,
          longitudeFinal: row.longitudeFinal,
          title: servicoTitle,
        })
        : [];
      const inicio = points.find((point) => point.title !== 'Fim') || null;
      const fim = points.find((point) => point.title === 'Fim') || null;
      const coordVisivel = inicio || fim;
      const distanciaTrecho = inicio && fim
        ? formatTrechoDistancia(inicio.lat, inicio.lng, fim.lat, fim.lng)
        : null;
      const openMap = (event) => {
        event.stopPropagation();
        if (!points.length) return;
        setMapPoint({
          title: servicoTitle,
          points,
          latitude: row.latitude,
          longitude: row.longitude,
          latitudeFinal: row.latitudeFinal,
          longitudeFinal: row.longitudeFinal,
        });
      };
      return (
        <>
          <TableCell>
            <Typography variant="body2" component="div" sx={{ lineHeight: 1.25 }}>
              {servicoTitle}
            </Typography>
            {!isServico && inspecionadoEm && (
              <Typography variant="caption" color="text.secondary" display="block">
                {inspecionadoEm}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            {isServico ? (
              <Box>
                {resultado ? (
                  <Chip size="small" label={label} color={color === 'default' ? 'default' : color} variant="outlined" />
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
                {inspecionadoEm && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {inspecionadoEm}
                  </Typography>
                )}
              </Box>
            ) : '—'}
          </TableCell>
          <TableCell>
            {isServico
              ? <ServicoCell value={row.servico || row.servicoInformado} />
              : '—'}
          </TableCell>
          <TableCell>
            {points.length > 0 ? (
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Tooltip title="Ver no mapa">
                  <IconButton size="small" onClick={openMap} aria-label="Ver no mapa">
                    <MapOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box>
                  {coordVisivel && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {`${coordVisivel.lat.toFixed(6)}, ${coordVisivel.lng.toFixed(6)}`}
                    </Typography>
                  )}
                  {distanciaTrecho && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {`Trecho · ${distanciaTrecho}`}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ) : '—'}
          </TableCell>
        </>
      );
    }
    if (row.codigo === '-999' && row.tipoServico === 'FO') {
      return (
        <>
          <TableCell />
          <TableCell>Serviço Georreferenciado</TableCell>
          <TableCell>-</TableCell>
          <TableCell><ServicoCell value={row.servico} /></TableCell>
          <TableCell>{getTipoServicoLabel(row)}</TableCell>
          <TableCell>-</TableCell>
          {showAuditoriaColumn && <TableCell>-</TableCell>}
        </>
      );
    }

    return (
      <>
        <TableCell sx={{ width: 20 }}>
          {row.prioridade && String(row.prioridade).toLowerCase() === '1' ? <UIconV2 /> : ''}
        </TableCell>
        <TableCell>{getRowTitle(row)}</TableCell>
        <TableCell>{row.prioridade}</TableCell>
        <TableCell><ServicoCell value={row.servico} /></TableCell>
        <TableCell>{getTipoServicoLabel(row)}</TableCell>
        <TableCell>
          {row.tipoServico && (
            <>
              <Typography variant="caption" fontWeight={700}>N° Nsmp Programada: </Typography>
              <Typography variant="caption">{isBlankString(row.nsmpProgramada)}</Typography>
              <br />
              <Typography variant="caption" fontWeight={700}>N° Nsmp Nova: </Typography>
              <Typography variant="caption">{isBlankString(row.nsmp)}</Typography>
            </>
          )}
        </TableCell>
        {showAuditoriaColumn && (
          <TableCell>
            {isServiceRow(row) && row.auditoria === true ? (
              <CheckCircleIcon fontSize="small" color="primary" />
            ) : ''}
          </TableCell>
        )}
      </>
    );
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const getFotos = async (fotos) => {
    const fotosList = [];
    for (let i in fotos) {
      fotosList.push({
        id: uuidv4(),
        nome: fotos[i].name,
        image: await toBase64(fotos[i]),
      });
    }
    return fotosList;
  };

  const isInspetor = () => nsirStatus === Status.EM_INSPECAO && inspetorId === userId;
  const isDigitador = () => isDigitadorEmDigitacao({
    nsirStatus,
    digitadorId,
    userId,
    userRole,
  });

  const permissionsCtx = {
    nsirStatus,
    inspetorId,
    userId,
    userRole,
    digitadorId,
    tipoServicoId,
    isAdmin: isAdmin(),
  };

  const showEdit = getServicosShowEdit(permissionsCtx) || canManageFaixaCriacao;
  const showView = getServicosShowView(permissionsCtx);
  const showViewRow = (row) => getServicosShowViewRow(row, {
    ...permissionsCtx,
    isServiceRow,
  });

  return (
    <>
      <Box>
        {isFaixa && isTrue(rfgImportadoViaOcr) && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            RFG importado por reconhecimento óptico. Revise coordenadas e quantidades antes de liberar a inspeção.
          </Alert>
        )}
        {!isFaixa && (
          <ServicosQuickFilters
            value={quickFilter}
            onChange={setQuickFilter}
            totalCount={(nsmps || []).length}
            filteredCount={filteredRows.length}
          />
        )}
        <TableWidgetV2
          rows={filteredRows}
          titles={tableTitles}
          headerTitle={`Serviços > ${nsirTitle}`}
          renderRows={renderRows}
          showEditRow={isServiceRow}
          hasRowActions
          onEdit={onEdit}
          onAdd={onAdd}
          onView={onView}
          onDelete={(id) => deleteNsmp(id)}
          showDelete={isInspetor() || canManageFaixaCriacao}
          showEdit={showEdit}
          showView={showView}
          showViewRow={showViewRow}
          hasAdd={nsirStatus === Status.EM_INSPECAO || canManageFaixaCriacao}
          extraHeaderActions={getHeaderActions()}
          onClearFilters={() => setQuickFilter(EMPTY_SERVICOS_QUICK_FILTER)}
          actions={[
            {
              key: 'clone',
              icon: <AddIcon />,
              show: (row) => isDigitador() && isServiceRow(row) && nsirStatus === Status.EM_DIGITACAO,
              onClick: (id) => {
                setOpenClone(true);
                setCloneId(id);
              },
            },
            {
              key: 'delete',
              icon: <DeleteIcon />,
              show: (row) => row.idNsmpReference && nsirStatus === Status.EM_DIGITACAO,
              onClick: (id) => {
                setOpenDeleteClone(true);
                setCloneId(id);
              },
            },
          ]}
        />
      </Box>

      <NsmpModal
        open={open}
        onCancel={handlerCancel}
        onConfirm={handlerConfirm}
        onConfirmAndNext={handlerConfirmAndNext}
        selectedId={selectedId}
        readOnly={readOnly}
        nsirTitle={nsirTitle}
        onNext={goToNext}
        onPrevious={goToPrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        carouselPosition={carouselPosition}
      />

      <DialogDefaultV2
        open={Boolean(mapPoint)}
        handlerCancel={() => setMapPoint(null)}
        title={mapPoint ? mapPoint.title : 'Localização'}
        maxWidth="md"
        fullWidth
        readOnly
      >
        {mapPoint && (
          <Stack spacing={1.5}>
            <GeoPointMap
              points={mapPoint.points}
              active={Boolean(mapPoint)}
              height={420}
              connect={mapPoint.points.length > 1}
            />
            <Box>
              {mapPoint.points.length > 1 ? mapPoint.points.map((point) => (
                <Typography key={point.id} variant="caption" color="text.secondary" display="block">
                  {`${point.title} · ${formatCoordDisplay(point.lat, point.lng)}`}
                </Typography>
              )) : (
                <Typography variant="caption" color="text.secondary">
                  {formatCoordDisplay(mapPoint.latitude, mapPoint.longitude)}
                </Typography>
              )}
            </Box>
            <Link
              href={
                googleMapsDirectionsUrl(
                  mapPoint.latitude,
                  mapPoint.longitude,
                  mapPoint.latitudeFinal,
                  mapPoint.longitudeFinal,
                )
                || googleMapsUrl(mapPoint.latitude, mapPoint.longitude)
                || googleMapsUrl(mapPoint.latitudeFinal, mapPoint.longitudeFinal)
                || undefined
              }
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
                alignSelf: 'flex-start',
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
              {mapPoint.points.length > 1 ? 'Abrir trecho no Google Maps' : 'Abrir no Google Maps'}
            </Link>
          </Stack>
        )}
      </DialogDefaultV2>

      <DropzoneModalV2
        onClose={() => setOpenFotoInicial(false)}
        open={openFotoInicial}
        onSave={async (fotos) => {
          setOpenFotoInicial(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '000',
            fotoInicio: true,
            fotoFim: false,
            analiseRisco: false,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />
      <DropzoneModalV2
        onClose={() => setOpenFotoFinal(false)}
        open={openFotoFinal}
        onSave={async (fotos) => {
          setOpenFotoFinal(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '999',
            fotoInicio: false,
            fotoFim: true,
            analiseRisco: false,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />
      <DropzoneModalV2
        onClose={() => setOpenAnaliseRisco(false)}
        open={openAnaliseRisco}
        onSave={async (fotos) => {
          setOpenAnaliseRisco(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '-001',
            fotoInicio: false,
            fotoFim: false,
            analiseRisco: true,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />

      <DialogDefaultV2
        open={openClone}
        handlerCancel={() => { setOpenClone(false); setCloneId(null); }}
        handlerConfirm={() => {
          cloneNsmp(cloneId, () => {
            setCloneId(null);
            setOpenClone(false);
          });
        }}
        title="Você tem certeza que deseja clonar o serviço??"
        confirmMessage="Clonar"
      >
        <DialogContentText>
          Ao clonar o serviço, todas as informações e fotos serão duplicadas. O novo serviço terá um novo código.
        </DialogContentText>
      </DialogDefaultV2>

      <DialogDefaultV2
        open={openDeleteClone}
        handlerCancel={() => { setOpenDeleteClone(false); setCloneId(null); }}
        handlerConfirm={() => {
          deleteCloneNsmp(cloneId, () => {
            setOpenDeleteClone(false);
            setCloneId(null);
          });
        }}
        title="Você tem que deseja remover o serviço?"
        confirmMessage="Remover"
      >
        <DialogContentText>
          A informação será removida e não poderá ser mais recuperado.
        </DialogContentText>
      </DialogDefaultV2>
    </>
  );
};

ServicesTab.propTypes = {
  nsmps: PropTypes.array,
  nsirId: PropTypes.string,
  updateNsmp: PropTypes.func.isRequired,
  saveNsmp: PropTypes.func.isRequired,
  cleanSelectedNsmp: PropTypes.func.isRequired,
  nsirStatus: PropTypes.string,
  userRole: PropTypes.string,
  deleteNsmp: PropTypes.func.isRequired,
  authData: PropTypes.object,
  userId: PropTypes.string,
  inspetorId: PropTypes.string,
  digitadorId: PropTypes.string,
  nsirTitle: PropTypes.string,
  tipoServicoId: PropTypes.string,
  newFlow: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rfgImportadoViaOcr: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  cloneNsmp: PropTypes.func.isRequired,
  deleteCloneNsmp: PropTypes.func.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userRole: selectorsAuth.getRole(state),
  userId: selectorsAuth.getUserId(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsNsmp }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ServicesTab);
